import { useLocation, useSearch } from "wouter";
import { useSession } from "../auth/session";
import { Card, EmptyState, InlineAlert, PageHeader, PageShell } from "../company-ui";
import { canOpen, currentItem, titleOf, type NavItem } from "./nav";

/**
 * A menu destination whose screen isn't built yet: its header and an honest empty state, so the
 * shell can be walked end to end. Each screen's own entry replaces it. A person whose roles can't
 * open the page (app-shell.md C) gets the page's no-permission state instead.
 */
export function NotYetPage({ item }: { item: NavItem }) {
  const { data } = useSession();
  const title = titleOf(item);
  if (!canOpen(item, data)) {
    return (
      <PageShell>
        <PageHeader title={title} />
        <InlineAlert variant="info" title={`You don't have access to ${inSentence(title)}. Ask an owner or controller.`} />
      </PageShell>
    );
  }
  return (
    <PageShell>
      <PageHeader title={title} subtitle={item.blurb} />
      <Card>
        <EmptyState title="Not built yet" description={`${title} is on the way. Nothing here is real data yet.`} />
      </Card>
    </PageShell>
  );
}

/** `/compliance?tab=…`: the tab's page (Book and tax when no tab is named). */
export function ComplianceNotYetPage() {
  const [location] = useLocation();
  const search = useSearch();
  const item = currentItem(location, search ? `?${search}` : "");
  return item ? <NotYetPage item={item} /> : null;
}

/** "Label rules" reads "label rules" mid-sentence; names and acronyms ("Texas", "TTB") keep their case. */
const inSentence = (t: string) => (/^[A-Z][a-z]/.test(t) && !/^Texas\b/.test(t) ? t[0]!.toLowerCase() + t.slice(1) : t);
